import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  SalesBriefingSection,
  StickyCtaBar,
  BriefingApplicationModal,
  FranchiseInquiryModal,
} from '@marketing/components'
import { useHashScroll } from '@/hooks/useHashScroll'
import { AboutLcHero } from '@/components/home/about-lc/AboutLcHero'
import { SelfDirectedSection } from '@/components/home/about-lc/SelfDirectedSection'
import { ContentTabsSection } from '@/components/home/about-lc/ContentTabsSection'
import { DiagnosisImageSection } from '@/components/home/about-lc/DiagnosisImageSection'
import { CourseLevelCards } from '@/components/home/about-lc/CourseLevelCards'
import { SystemTitleSection } from '@/components/home/about-lc/SystemTitleSection'
import { LcLearningStepBlock } from '@/components/home/about-lc/LcLearningStepBlock'
import { LcTestInfoBlock } from '@/components/home/about-lc/LcTestInfoBlock'
import { OutcomeCardsSection } from '@/components/home/about-lc/OutcomeCardsSection'
import { UnitSpecialSection } from '@/components/home/about-lc/UnitSpecialSection'
import { getAboutLcContent } from './aboutLc.content'

export default function AboutLcPage() {
  // Rebuild copy on language changes; asset URLs and business values stay unchanged.
  const { t: mt } = useTranslation('marketing')
  const c = getAboutLcContent(mt)
  const navigate = useNavigate()
  const location = useLocation()
  const [briefingOpen, setBriefingOpen] = useState(false)
  const [franchiseOpen, setFranchiseOpen] = useState(false)

  const handleBriefing = () => setBriefingOpen(true)
  const handleInquiry = () => setFranchiseOpen(true)
  const handleHeroCta = () => navigate('/franchise')

  const handleBriefingClose = () => {
    setBriefingOpen(false)
    const params = new URLSearchParams(location.search)
    if (params.has('briefing')) {
      params.delete('briefing')
      const search = params.toString()
      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : '',
          hash: location.hash,
        },
        { replace: true },
      )
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('briefing') === '1') {
      setBriefingOpen(true)
    }
  }, [location.search])

  useHashScroll()

  return (
    <>
      {/* #01 Hero */}
      <AboutLcHero
        label={c.hero.label}
        bodyLine1={c.hero.bodyLine1}
        bodyLine2={c.hero.bodyLine2}
        emphasis={c.hero.emphasis}
        ctaLabel={c.hero.ctaLabel}
        onCtaClick={handleHeroCta}
        imageUrl={c.hero.imageUrl}
        imageUrlMobile={c.hero.imageUrlMobile}
        imageAlt={c.hero.imageAlt}
      />

      {/* #02 Self-directed learning */}
      <SelfDirectedSection
        titlePrimaryA={c.selfDirected.titlePrimaryA}
        titlePrimaryB={c.selfDirected.titlePrimaryB}
        titleHighA={c.selfDirected.titleHighA}
        titleHighB={c.selfDirected.titleHighB}
        body={c.selfDirected.body}
        emphasis={c.selfDirected.emphasis}
        imageUrl={c.selfDirected.imageUrl}
        imageUrlMobile={c.selfDirected.imageUrlMobile}
        imageAlt={c.selfDirected.imageAlt}
      />

      {/* #03 Content tabs */}
      <ContentTabsSection
        titleHighDesktop={c.contentTabs.titleHighDesktop}
        titlePrimaryDesktop={c.contentTabs.titlePrimaryDesktop}
        titleHighMobileA={c.contentTabs.titleHighMobileA}
        titleHighMobileB={c.contentTabs.titleHighMobileB}
        titlePrimaryMobileA={c.contentTabs.titlePrimaryMobileA}
        titlePrimaryMobileB={c.contentTabs.titlePrimaryMobileB}
        tabs={c.contentTabs.tabs}
      />

      {/* #04 Diagnosis */}
      <DiagnosisImageSection
        titleHigh={c.diagnosis.titleHigh}
        titlePrimary={c.diagnosis.titlePrimary}
        titlePrimaryMobileA={c.diagnosis.titlePrimaryMobileA}
        titlePrimaryMobileB={c.diagnosis.titlePrimaryMobileB}
        note={c.diagnosis.note}
        imageUrl={c.diagnosis.imageUrl}
        imageUrlMobile={c.diagnosis.imageUrlMobile}
        imageAlt={c.diagnosis.imageAlt}
      />

      {/* #05 Courses A~E */}
      <CourseLevelCards
        titleHigh={c.courses.titleHigh}
        titlePrimary={c.courses.titlePrimary}
        titleHighMobileA={c.courses.titleHighMobileA}
        titleHighMobileB={c.courses.titleHighMobileB}
        titlePrimaryMobileA={c.courses.titlePrimaryMobileA}
        titlePrimaryMobileB={c.courses.titlePrimaryMobileB}
        descPart1={c.courses.descPart1}
        descEmphasis={c.courses.descEmphasis}
        descPart2={c.courses.descPart2}
        levels={c.courses.levels}
      />

      {/* #06 System title */}
      <SystemTitleSection
        line1High={c.systemTitle.line1High}
        line2Primary={c.systemTitle.line2Primary}
        line1HighMobileA={c.systemTitle.line1HighMobileA}
        line1HighMobileB={c.systemTitle.line1HighMobileB}
        line2PrimaryMobileA={c.systemTitle.line2PrimaryMobileA}
        line2PrimaryMobileB={c.systemTitle.line2PrimaryMobileB}
      />

      {/* #07 Today learning — 5 step tabs (BIG BLUE) */}
      <LcLearningStepBlock
        badge={c.todayLearning.badge}
        title={c.todayLearning.title}
        description={c.todayLearning.description}
        note={c.todayLearning.note}
        tabs={c.todayLearning.tabs}
      />

      {/* #08 Chapter test — blue/800 dark */}
      <LcTestInfoBlock
        badge={c.chapterTest.badge}
        title={c.chapterTest.title}
        description={c.chapterTest.description}
        panelTitle={c.chapterTest.panelTitle}
        rows={c.chapterTest.rows}
        imageUrl={c.chapterTest.imageUrl}
        imageUrlMobile={c.chapterTest.imageUrlMobile}
        imageAlt={c.chapterTest.imageAlt}
        bgColor="#1c52a8"
        topColor="#4599ff"
        sectionId="chapter-test"
      />

      {/* #09 Cumulative test — navy darker */}
      <LcTestInfoBlock
        badge={c.cumulativeTest.badge}
        title={c.cumulativeTest.title}
        description={c.cumulativeTest.description}
        panelTitle={c.cumulativeTest.panelTitle}
        rows={c.cumulativeTest.rows}
        imageUrl={c.cumulativeTest.imageUrl}
        imageUrlMobile={c.cumulativeTest.imageUrlMobile}
        imageAlt={c.cumulativeTest.imageAlt}
        bgColor="#16224f"
        topColor="#1c52a8"
        sectionId="cumulative-test"
      />

      {/* #10 Outcome cards */}
      <OutcomeCardsSection
        titleHigh={c.outcomes.titleHigh}
        titlePrimary={c.outcomes.titlePrimary}
        titleHighMobileA={c.outcomes.titleHighMobileA}
        titleHighMobileB={c.outcomes.titleHighMobileB}
        titlePrimaryMobileA={c.outcomes.titlePrimaryMobileA}
        titlePrimaryMobileB={c.outcomes.titlePrimaryMobileB}
        cards={c.outcomes.cards}
      />

      {/* #11 Unit-special videos */}
      <UnitSpecialSection
        titlePrimary={c.unitSpecial.titlePrimary}
        titleHigh={c.unitSpecial.titleHigh}
        description={c.unitSpecial.description}
        descriptionMobileA={c.unitSpecial.descriptionMobileA}
        descriptionMobileB={c.unitSpecial.descriptionMobileB}
        note={c.unitSpecial.note}
        slides={c.unitSpecial.slides}
      />

      {/* #12 Sales briefing — shared */}
      <SalesBriefingSection
        onBriefingClick={handleBriefing}
        onInquiryClick={() => navigate('/franchise')}
      />

      <StickyCtaBar
        onBriefingClick={handleBriefing}
        onInquiryClick={handleInquiry}
      />

      <BriefingApplicationModal
        open={briefingOpen}
        onClose={handleBriefingClose}
      />
      <FranchiseInquiryModal
        open={franchiseOpen}
        onClose={() => setFranchiseOpen(false)}
      />
    </>
  )
}
